import React from "react";

// Cache global (module-level) : stability with virtual scroll on PrimeReact
const svgCache = new Map(); // url -> svgText
const pendingCache = new Map(); // url -> Promise<string>

function buildClassName({ color, fill, dimensions, stroke }) {
    let cls = "icon";
    if (color) {cls += ` icon-color-${color}`;}
    if (fill) {cls += ` icon-fill-${color}`;}
    if (stroke) {cls += ` icon-stroke-width${stroke}`;}
    if (dimensions) {cls += ` icon-height${dimensions}`;}
    return cls;
}

async function fetchSvg(url) {
    let p = pendingCache.get(url);
    if (!p) {
        p = fetch(url)
            .then((res) => {
                if (!res.ok) {throw new Error(`Icon fetch failed: ${res.status}`);}
                return res.text();
            })
            .then((text) => {
                if (!text.includes("<svg")) {throw new Error("Not an SVG payload");}
                svgCache.set(url, text);
                return text;
            })
            .finally(() => {
                pendingCache.delete(url);
            });

        pendingCache.set(url, p);
    }
    return p;
}

const IconComponent = React.memo(function IconComponent({
    nameIcon,
    color,
    fill,
    dimensions,
    stroke,
    folder
}) {
    if (typeof nameIcon !== "string" || nameIcon.trim() === "") {return <span />;}

    const basePath = `/aGlobalDesign/${folder ? folder : "icons"}`;
    const fileName = nameIcon.endsWith(".svg") ? nameIcon : `${nameIcon}.svg`;
    const urlIcon = `${basePath}/${fileName}`;

    const className = buildClassName({ color, fill, dimensions, stroke });
    const sizePx = dimensions ? `${dimensions}px` : "24px"; // default safe size

    const [svgHtml, setSvgHtml] = React.useState(() => svgCache.get(urlIcon) ?? null);
    const [fallbackSrc, setFallbackSrc] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;

        // If cache -> Display immediatly (avoid displaying the button then icon)
        const cached = svgCache.get(urlIcon);
        if (cached) {
            setSvgHtml(cached);
            setFallbackSrc(null);
            return () => { cancelled = true; };
        }

        setSvgHtml(null);
        setFallbackSrc(null);

        fetchSvg(urlIcon)
            .then((text) => {
                if (cancelled) {return;}
                setSvgHtml(text);
                setFallbackSrc(null);
            })
            .catch(() => {
                if (cancelled) {return;}
                setSvgHtml(null);
                setFallbackSrc(urlIcon);
            });

        return () => {
            cancelled = true;
        };
    }, [urlIcon]);

    // Fallback image (rare, only if fetch error / no-SVG)
    if (fallbackSrc) {
        return (
            <img
                className={className}
                alt={nameIcon}
                src={fallbackSrc}
                style={{ width: sizePx, height: sizePx, display: "block" }}
                loading="lazy"
                decoding="async"
            />
        );
    }

    // Placeholder immediatly : reserves the spot even before the icon arrives
    return (
        <span
            className={className}
            style={{
                width: sizePx,
                height: sizePx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0
            }}
        >
            {svgHtml ?
                <span
                    style={{ width: "100%", height: "100%", display: "inline-block" }}
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                />
                :
                <span aria-hidden="true" style={{ width: "100%", height: "100%", display: "inline-block" }} />
            }
        </span>
    );
});

export default IconComponent;
